// Per-surface copy for the Starter-plan Marketing engagement / upsell pages (PE-1651).
//
// CAPABILITY-FIRST by design: there is intentionally NO data-insight stat band here.
// Showing the org its own numbers (disengaged/LYBUNT, reachable-by-email, recurring)
// needs a new org-scoped counts endpoint that does not exist yet — see the spike result
// in docs/initiatives/new/pocketflows/linear-milestones/marketing-engagement-upsell/IMPLEMENTATION.md §3. The band
// is a gated fast-follow; these pages ship value-via-capability now (also the
// empty/small-org fallback, since most Starter orgs are small).
//
// Copy mirrors the approved mockup (.../marketing-engagement-upsell/mockups/
// marketing-engagement.html). RECONCILE these labels against the shipped Broadcasts /
// Segments / Email Templates UI before merge (PocketFlows distinguishes Campaign /
// Email Blast / Trigger; the shipped table labels win).
//
// These pages are the STARTER-plan upsell — Starter has neither marketing nor SMS.
// (Pre-PE-1860 note, now superseded: Base included marketing but 0 SMS, so the copy
// was email-first "SMS starts at Plus". As of PE-1860, 2026-07-26, Base includes 250
// SMS/mo, so SMS is no longer a Plus-only differentiator — the lowest SMS tier is
// engine-derived at the call site via unlockTierLabel(BROADCASTS_SMS). Do not
// hard-code "Plus" as the SMS tier in this copy.)

export type MarketingSurfaceKey =
  | 'broadcasts'
  | 'segments'
  | 'email_templates'
  | 'settings'
  | 'settings_sms'
  | 'donation_impact';

export type PreviewTagTone = 'campaign' | 'trigger' | 'locked';

export interface PreviewMeta {
  value: string;
  label: string;
  /** Render the value in the sage accent (for the headline number). */
  emphasize?: boolean;
}

export interface PreviewCard {
  title: string;
  tag: string;
  tone: PreviewTagTone;
  meta: PreviewMeta[];
}

// Icon shown in a benefit card's tinted square. Closed union (not a free
// string) so a typo surfaces at compile time; mapped to a Heroicon component in
// MarketingEngagement (presentation stays in the component, copy stays here).
export type BenefitIconKey =
  | 'megaphone'
  | 'bolt'
  | 'chart'
  | 'users'
  | 'refresh'
  | 'send'
  | 'sparkles'
  | 'layout'
  | 'brand';

export interface BenefitItem {
  title: string;
  description: string;
  icon: BenefitIconKey;
}

export interface SurfaceContent {
  /** Headline — value-led, not "what the tier is." */
  title: string;
  /** One-line, plain-language pitch. */
  pitch: string;
  /** "What you can do" cards. Omitted for `simple` surfaces. */
  benefits?: BenefitItem[];
  /** Right-column illustrative cards. Mutually exclusive with `exampleEmail`. */
  preview?: PreviewCard[];
  /**
   * Public-asset path to a product screenshot for the right column. Takes
   * precedence over `preview` cards / `exampleEmail` when set. Lives in
   * `public/assets/marketing/` and is referenced by root URL (same convention as
   * the product-tour previews in tourSteps.js — no import, so a missing file
   * just shows a broken <img>, it does not break the build).
   */
  screenshot?: string;
  /** Templates: render an example AI-drafted email instead of preview cards. */
  exampleEmail?: boolean;
  /**
   * Strip down to a one-line message + CTA (no screenshot / benefits). Used by
   * `settings_sms` (the SMS sub-tab, a narrower column alongside the tab nav).
   * `settings` itself dropped this in PE-1736 to match the Broadcasts/Segments/
   * Email Templates hero treatment.
   */
  simple?: boolean;
}

export const MARKETING_ENGAGEMENT_CONTENT: Record<MarketingSurfaceKey, SurfaceContent> = {
  broadcasts: {
    title: 'Reach donors you already have',
    pitch:
      'Send a scheduled appeal to a group of donors, or set up emails that send themselves — powered by the donation data already in CharityStack.',
    // Product screenshot of the campaign builder. Save the image to
    // packages/dashboard/public/assets/marketing/broadcasts-preview.png
    // (or swap the extension to .webp here if you optimize it).
    screenshot: '/assets/marketing/broadcasts-preview.png',
    // No `preview` cards — the screenshot is the broadcasts visual now.
    benefits: [
      {
        title: 'Campaigns',
        description: 'Schedule an appeal to a donor group — a Ramadan or year-end push.',
        icon: 'megaphone',
      },
      {
        title: 'Triggers',
        description:
          'Emails that send themselves — a welcome on a first gift, a nudge when a donor goes quiet.',
        icon: 'bolt',
      },
      {
        title: 'Payment Impact',
        description: 'See which email drove each gift, not just who opened it.',
        icon: 'chart',
      },
    ],
  },

  segments: {
    title: 'Choose the segment for your next message',
    pitch:
      'Start with a template, review its rules, and save only the groups you need. Build your own segments from scratch. Saved segments stay current as your donor data changes.',
    // Product screenshot of the segments table (same 1/3 text + 2/3 image hero
    // as broadcasts). Lives at public/assets/marketing/segments-preview.png.
    screenshot: '/assets/marketing/segments-preview.png',
    benefits: [
      {
        title: 'Built from your data',
        description: 'Recent, recurring, disengaged, major-donor groups — no spreadsheets.',
        icon: 'users',
      },
      {
        title: 'Always current',
        description: 'Donors move between groups automatically as their giving changes.',
        icon: 'refresh',
      },
      {
        title: 'Target any broadcast',
        description: 'Email just the donors who matter — a win-back, or a thank-you.',
        icon: 'send',
      },
    ],
  },

  email_templates: {
    title: 'Make a branded email in seconds',
    pitch:
      'Tell the AI what you want to say — it drafts a professional, on-brand email, ready to send. Fine-tune anything in a drag-and-drop builder.',
    // Product screenshot of the email builder (same 1/3 text + 2/3 image hero as
    // broadcasts). Lives at public/assets/marketing/email-templates-preview.png.
    screenshot: '/assets/marketing/email-templates-preview.png',
    benefits: [
      {
        title: 'AI drafting',
        description: 'Describe your goal — get a polished, on-brand email in seconds.',
        icon: 'sparkles',
      },
      {
        title: 'Drag-and-drop builder',
        description: 'Tweak anything visually. No code, no blank page.',
        icon: 'layout',
      },
      {
        title: 'On-brand automatically',
        description: 'Your logo, colors, and voice applied for you.',
        icon: 'brand',
      },
    ],
  },

  settings: {
    title: 'Marketing settings',
    pitch:
      'Your brand, sending email, and templates live here. Set them up when you upgrade — we handle deliverability for you.',
    // PE-1736 — Settings gets the same rich hero as Broadcasts / Segments /
    // Email Templates (was `simple: true`; the original PE-1651 spec kept
    // Settings minimal, but product now wants the same image + benefit-cell
    // treatment here). No Settings-specific screenshot exists yet, so this
    // reuses the Broadcasts preview — Settings is where the sending brand and
    // domain that Broadcasts uses gets configured, so it's a reasonable stand-in
    // until a dedicated Settings screenshot is captured.
    screenshot: '/assets/marketing/broadcasts-preview.png',
    benefits: [
      {
        title: 'Your own sending domain',
        description: 'Emails send from your brand, not a generic address — better deliverability too.',
        icon: 'brand',
      },
      {
        title: 'A dedicated SMS number',
        description: 'Text supporters from a number that is yours, with credits and opt-outs managed here.',
        icon: 'send',
      },
      {
        title: 'Branded templates, ready to go',
        description: 'Your logo, colors, and voice applied automatically to every email you send.',
        icon: 'layout',
      },
    ],
  },

  // SMS tab on a plan without the broadcasts_sms entitlement (STARTER only, post-
  // PE-1860). A simple upgrade-led panel that previews the value rather than the
  // request form. The specific unlock tier is NOT named here — the <Pill> renders
  // "Available on the {unlockTier} plan" from the engine-derived prop, so this copy
  // stays correct whichever tier is the lowest SMS tier.
  settings_sms: {
    simple: true,
    title: 'Text your supporters',
    pitch:
      'SMS lets you reach donors where they already are. Upgrade to request a dedicated sending number and add text to your broadcasts — your plan includes a monthly SMS allotment, then it’s metered per message.',
  },

  // Payment Impact — the Marketing tab of the Home analytics panel. Copy mirrors
  // what that panel shows (attributed revenue, payments driven, campaign
  // performance, the send→pay funnel). Preview cards stand in for the metric
  // cards since there's no screenshot for this surface.
  donation_impact: {
    title: 'See what your marketing actually raised',
    pitch:
      'Payment Impact ties every email and text to the payments it drove — revenue attributed, payments driven, and which campaigns worked — right here on your dashboard.',
    // Screenshot of the Marketing analytics tab (same 1/3 text + 2/3 image hero
    // as the other surfaces). Lives at public/assets/marketing/donation-impact-preview.png.
    screenshot: '/assets/marketing/donation-impact-preview.png',
    benefits: [
      {
        title: 'Revenue attribution',
        description:
          'See the dollars each campaign and trigger brought in — not just opens and clicks.',
        icon: 'chart',
      },
      {
        title: 'Payments driven',
        description: 'Count the actual payments your marketing produced, with average payment size.',
        icon: 'send',
      },
      {
        title: 'Send → pay funnel',
        description: 'Follow contacts from sent to opened to paid, and spot your top campaigns.',
        icon: 'megaphone',
      },
    ],
  },

};
